import { Authenticator, ThemeProvider, Theme, useAuthenticator } from '@aws-amplify/ui-react';
import { useContext, useEffect, useMemo } from 'react';
import { AgencyContext } from './App';
import usePageTitle from './hooks/usePageTitle';
import '@aws-amplify/ui-react/styles.css';
import './Login.css';
import appTheme from './theme';
import { alpha } from '@mui/system';
import { CircularProgress } from '@mui/material';

// Light-theme colors pulled from the app theme so the login page matches the rest of the platform
const textPrimaryColor = appTheme.palette.text.primary;
const inputBorderColor = appTheme.palette.divider;
const pageBackground = appTheme.palette.grey['50'];
const linkButtonBackground = {
    hover: alpha(appTheme.palette.primary.main, appTheme.palette.action.hoverOpacity),
    active: alpha(appTheme.palette.primary.main, appTheme.palette.action.activatedOpacity),
};

export default function Login() {
    const { authStatus } = useAuthenticator((context) => [context.authStatus]);
    const agency = useContext(AgencyContext);

    usePageTitle({ segments: ['Login'] });

    const isWhiteLabelled = agency?.whiteLabelPortalEnabled && !!agency?.supportEmail;
    // Brand name for the logo alt text: the agency resolved for this host, or the platform default.
    const brandName = agency?.name?.trim() || '[cognition]';

    const components = useMemo(
        () => ({
            Header() {
                // While the host lookup is still in flight (agency === undefined) show a spinner so
                // we don't flash the [cognition] logo before a white-label logo resolves. Once
                // resolved, render the agency's white-label logo when present, otherwise the
                // platform's default dark logo.
                if (agency === undefined) {
                    return (
                        <div className="Login-logo">
                            <CircularProgress color="info" />
                        </div>
                    );
                }

                return (
                    <div className="Login-logo">
                        <img alt={brandName} src={agency?.logoUrl ? agency.logoUrl : '/Logo_Black_iCAN.svg'} />
                    </div>
                );
            },
            SignIn: {
                Footer() {
                    const { toResetPassword } = useAuthenticator();

                    if (isWhiteLabelled) {
                        const subject = encodeURIComponent('Password Reset Request');
                        const body = encodeURIComponent(
                            'Hello,\n\nI would like to request a password reset for my account.\n\nThank you.'
                        );
                        return (
                            <div style={{ textAlign: 'center', padding: '0 32px 16px' }}>
                                <a
                                    href={`mailto:${agency.supportEmail}?subject=${subject}&body=${body}`}
                                    style={{
                                        color: appTheme.palette.primary.main,
                                        textDecoration: 'none',
                                        fontSize: '14px',
                                    }}
                                >
                                    Forgot your password?
                                </a>
                            </div>
                        );
                    }

                    return (
                        <div style={{ textAlign: 'center', padding: '0 32px 16px' }}>
                            <button
                                onClick={toResetPassword}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: appTheme.palette.primary.main,
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    padding: 0,
                                }}
                            >
                                Forgot your password?
                            </button>
                        </div>
                    );
                },
            },
        }),
        [agency, isWhiteLabelled, brandName]
    );

    useEffect(() => {
        if (authStatus === 'authenticated') {
            localStorage.setItem('X-COGNITION-TRIALFIRE', '1');
        }
    }, [authStatus]);

    const theme: Theme = {
        name: 'Login theme',
        tokens: {
            colors: {
                background: {
                    primary: {
                        value: pageBackground,
                    },
                },
                font: {
                    active: {
                        value: textPrimaryColor,
                    },
                    interactive: {
                        value: textPrimaryColor,
                    },
                },
                brand: {
                    primary: {
                        '100': appTheme.palette.primary.main,
                    },
                },
            },
            components: {
                authenticator: {
                    router: {
                        borderWidth: '0px',
                        boxShadow: 'none',
                    },
                    form: {
                        padding: '16px 32px',
                    },
                },
                heading: {
                    color: textPrimaryColor,
                },
                field: {
                    label: {
                        color: textPrimaryColor,
                    },
                },
                fieldcontrol: {
                    borderColor: inputBorderColor,
                },
                textfield: {
                    borderColor: inputBorderColor,
                    color: textPrimaryColor,
                },
                button: {
                    color: textPrimaryColor,
                    primary: {
                        backgroundColor: appTheme.palette.primary.main,
                        color: appTheme.palette.primary.contrastText,
                        _hover: {
                            color: appTheme.palette.primary.contrastText,
                            backgroundColor: appTheme.palette.primary.dark,
                        },
                        _active: {
                            color: appTheme.palette.primary.contrastText,
                            backgroundColor: alpha(appTheme.palette.primary.dark, 0.8),
                        },
                        _focus: {
                            color: appTheme.palette.primary.contrastText,
                            backgroundColor: alpha(appTheme.palette.primary.dark, 0.8),
                        },
                    },
                    link: {
                        _hover: {
                            backgroundColor: linkButtonBackground.hover,
                            color: textPrimaryColor,
                        },
                        _active: {
                            backgroundColor: linkButtonBackground.active,
                            color: textPrimaryColor,
                        },
                        _focus: {
                            backgroundColor: linkButtonBackground.active,
                            color: textPrimaryColor,
                        },
                    },
                    _hover: {
                        backgroundColor: linkButtonBackground.hover,
                        borderColor: appTheme.palette.grey[400],
                        color: textPrimaryColor,
                    },
                },
            },
        },
    };

    return (
        <div
            style={{
                width: '100%',
                height: '100vh',
                backgroundColor: pageBackground,
            }}
        >
            <ThemeProvider theme={theme}>
                <Authenticator className="Login" components={components} hideSignUp />
            </ThemeProvider>
        </div>
    );
}
